import {
  Product,
  ProductDimensionComponent,
} from "../../common/models/product";

export const calculateMaterialVolume = (
  product: Product,
): number | undefined => {
  if (!product.standardDimensions) return 0;

  const sd = product.standardDimensions;

  const toMM = (v: { hi: ProductDimensionComponent }) => {
    // specific to standard dimensions
    return v.hi.value;
  };

  if (sd.length && sd.width && sd.height) {
    return toMM(sd.length) * toMM(sd.width) * toMM(sd.height);
  }

  if (sd.length && sd.width && sd.thickness) {
    return toMM(sd.length) * toMM(sd.width) * toMM(sd.thickness);
  }

  if (sd.diameter && sd.length && sd.inner_diameter) {
    const outerRadius = toMM(sd.diameter) / 2;
    const innerRadius = toMM(sd.inner_diameter) / 2;
    return (
      Math.PI *
      (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2)) *
      toMM(sd.length)
    );
  }

  if (sd.outer_diameter && sd.length && sd.inner_diameter) {
    const outerRadius = toMM(sd.outer_diameter) / 2;
    const innerRadius = toMM(sd.inner_diameter) / 2;
    return (
      Math.PI *
      (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2)) *
      toMM(sd.length)
    );
  }

  if (sd.diameter && sd.length && sd.wall_thickness) {
    const outerRadius = toMM(sd.diameter) / 2;
    const innerRadius = outerRadius - toMM(sd.wall_thickness);
    return (
      Math.PI *
      (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2)) *
      toMM(sd.length)
    );
  }
  if (sd.outer_diameter && sd.length && sd.wall_thickness) {
    const outerRadius = toMM(sd.outer_diameter) / 2;
    const innerRadius = outerRadius - toMM(sd.wall_thickness);
    return (
      Math.PI *
      (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2)) *
      toMM(sd.length)
    );
  }
  if (sd.inner_diameter && sd.length && sd.wall_thickness) {
    const innerRadius = toMM(sd.inner_diameter) / 2;
    const outerRadius = innerRadius + toMM(sd.wall_thickness);
    return (
      Math.PI *
      (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2)) *
      toMM(sd.length)
    );
  }

  if (sd.length && sd.width && product.shape === "hex bar") {
    const width = toMM(sd.width);
    return ((3 * Math.sqrt(3)) / 2) * Math.pow(width, 2) * toMM(sd.length);
  }

  if (sd.diameter && sd.length) {
    const radius = toMM(sd.diameter) / 2;
    return Math.PI * Math.pow(radius, 2) * toMM(sd.length);
  }
};

/**
 * Cross-section area in mm². Used to convert $/lb ↔ $/ft for bars sold by
 * random / cut-to-length footage: the buyer's requested feet is the quantity,
 * and stock length on the listing is not required when diameter (or OD/ID)
 * alone defines the section.
 */
export const crossSectionAreaMm2 = (product: Product): number | null => {
  if (!product.standardDimensions) return null;
  const sd = product.standardDimensions;
  const has = (v: any): boolean => typeof v?.hi?.value === "number";
  const toMM = (v: { hi: ProductDimensionComponent }) => v.hi.value;

  // When stock length is on the record, keep the established volume/length path
  // so hollow sections and hex bars stay consistent with calculateMaterialVolume.
  const volume = calculateMaterialVolume(product);
  if (volume && has(sd.length) && toMM(sd.length) > 0) {
    return volume / toMM(sd.length);
  }

  if (has(sd.diameter) && !has(sd.inner_diameter) && !has(sd.wall_thickness)) {
    const radius = toMM(sd.diameter) / 2;
    return Math.PI * Math.pow(radius, 2);
  }

  if (has(sd.outer_diameter) && has(sd.inner_diameter)) {
    const outerRadius = toMM(sd.outer_diameter) / 2;
    const innerRadius = toMM(sd.inner_diameter) / 2;
    return Math.PI * (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2));
  }

  if (has(sd.diameter) && has(sd.inner_diameter)) {
    const outerRadius = toMM(sd.diameter) / 2;
    const innerRadius = toMM(sd.inner_diameter) / 2;
    return Math.PI * (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2));
  }

  if (has(sd.outer_diameter) && has(sd.wall_thickness)) {
    const outerRadius = toMM(sd.outer_diameter) / 2;
    const innerRadius = outerRadius - toMM(sd.wall_thickness);
    if (innerRadius <= 0) return null;
    return Math.PI * (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2));
  }

  if (has(sd.diameter) && has(sd.wall_thickness)) {
    const outerRadius = toMM(sd.diameter) / 2;
    const innerRadius = outerRadius - toMM(sd.wall_thickness);
    if (innerRadius <= 0) return null;
    return Math.PI * (Math.pow(outerRadius, 2) - Math.pow(innerRadius, 2));
  }

  if (product.shape === "hex bar" && has(sd.width)) {
    const width = toMM(sd.width);
    return ((3 * Math.sqrt(3)) / 2) * Math.pow(width, 2);
  }

  return null;
};
